/**
 * Style Genome — data-driven validator.
 *
 * Validates a genome purely by iterating the field spec (no per-field logic):
 * every required dimension present, every value drawn from its vocabulary,
 * lists are arrays of vocabulary members, and expansion rules conform.
 * Deterministic and non-throwing.
 */

import { GENOME_FIELDS, EXPANSION_FIELDS, IDENTITY_FIELDS } from './genomeSchema';
import { COMPONENT_FAMILIES } from './vocabulary';

const isArr = Array.isArray;
const isStr = (v) => typeof v === 'string' && v.length > 0;

/** Validate a value (single or list) against a field spec. Returns error strings. */
const validateField = (obj, spec, prefix) => {
  const errors = [];
  const value = obj[spec.key];
  const path = `${prefix}${spec.key}`;

  if (value === undefined || value === null) {
    if (spec.required) errors.push(`${path}: required`);
    return errors;
  }

  // Identity fields without a vocab (id/name) only need to be non-empty strings.
  if (!spec.vocab) {
    if (!isStr(value)) errors.push(`${path}: must be a non-empty string`);
    return errors;
  }

  if (spec.list) {
    if (!isArr(value) || value.length === 0) { errors.push(`${path}: must be a non-empty array`); return errors; }
    value.forEach((v, i) => {
      if (!spec.vocab.includes(v)) errors.push(`${path}[${i}]: "${v}" is not in the allowed vocabulary`);
    });
  } else if (!spec.vocab.includes(value)) {
    errors.push(`${path}: "${value}" is not in the allowed vocabulary`);
  }
  return errors;
};

/**
 * Validate one genome.
 * @param {*} genome
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateGenome = (genome) => {
  const errors = [];
  if (genome === null || typeof genome !== 'object' || isArr(genome)) {
    return { valid: false, errors: ['genome must be an object'] };
  }

  IDENTITY_FIELDS.forEach((spec) => errors.push(...validateField(genome, spec, '')));
  GENOME_FIELDS.forEach((spec) => errors.push(...validateField(genome, spec, '')));

  const exp = genome.expansion;
  if (exp === undefined || exp === null || typeof exp !== 'object' || isArr(exp)) {
    errors.push('expansion: required object');
  } else {
    EXPANSION_FIELDS.forEach((spec) => errors.push(...validateField(exp, spec, 'expansion.')));
    if (exp.componentBias !== undefined) {
      if (typeof exp.componentBias !== 'object' || exp.componentBias === null || isArr(exp.componentBias)) {
        errors.push('expansion.componentBias: must be an object');
      } else {
        Object.entries(exp.componentBias).forEach(([family, tag]) => {
          if (!COMPONENT_FAMILIES.includes(family)) errors.push(`expansion.componentBias.${family}: unknown component family`);
          if (!isStr(tag)) errors.push(`expansion.componentBias.${family}: bias tag must be a non-empty string`);
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
};
